import React, { useContext, useEffect, useState, useRef } from 'react';
import { Volume2, Loader, X, BookOpen, BookMarked, Plus } from 'lucide-react';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';
import { useWordDetails } from '../hooks/useWordDetails';
import type { WordPopoverProps } from '../types';
import { Tooltip } from 'react-tooltip';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../store/useStore';
import { AuthContext } from '../contexts/AuthContext';
import Modal from './Modal';

// Cache to avoid repeatedly retrying audio URLs that previously failed in the session
const failedAudioUrls = new Set<string>();

export function WordPopover({ 
  word, 
  context, 
  onClose, 
  position, 
  useRelativePosition = false, 
  inline = false,
  isExpression = false,
  sessionId,
  sessionPartner,
  onSaveToSession
}: WordPopoverProps) {
  const navigate = useNavigate();
  
  // Only strip spaces if it is NOT an expression
  const sanitizedWord = isExpression ? word : word.replace(/\s/g, '')
    .trim()
    .replace(',', '')
    .replace('.', '')
    .replace(/[.,!?]/g, '')
    .replace(/―/g, '')
    .replace('...', '')
    .replace(' ', '')
    .replace(';', '')
    .replace(/'s\b/g, 's')
    .replace(/'re\b/g, ' are')
    .replace(/\s+/g, ' ');

  const { details, isLoading, error } = useWordDetails(sanitizedWord, context);
  const { speak } = useSpeechSynthesis({ lang: 'en-US' });
  const [wordSaved, setWordSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loginModal, setLoginModal] = useState(false);
  const [sessionAssignee, setSessionAssignee] = useState<'me' | 'friend' | 'both'>('both');
  const { addWord, getWord, addExpression, getExpression } = useContext(UserContext)
  const { currentUser } = useContext(AuthContext)
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (inline) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        if (onClose) onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, inline]);

  const handleSpeak = (accentLang: 'en-US' | 'en-GB' = 'en-US') => {
    const rawAudio = details?.audio?.trim();
    const cleanAudio = rawAudio ? (rawAudio.startsWith('//') ? `https:${rawAudio}` : rawAudio) : null;
    const isUkAudio = cleanAudio ? /(-uk|-gb|_uk|_gb)[._\-\d]*\.(mp3|ogg|wav)($|\?)/i.test(cleanAudio) : false;
    const isFailedUrl = cleanAudio ? failedAudioUrls.has(cleanAudio) : false;
    const shouldUseAudioFile = Boolean(
      cleanAudio && !isFailedUrl && ((accentLang === 'en-US' && !isUkAudio) || (accentLang === 'en-GB' && isUkAudio))
    );

    if (shouldUseAudioFile && cleanAudio) {
      const nextAudio = new Audio(cleanAudio);
      let hasFallenBack = false;
      const fallbackToSpeech = (err?: any) => {
        if (hasFallenBack) return;
        hasFallenBack = true;
        failedAudioUrls.add(cleanAudio);
        speak(word, accentLang);
      };

      nextAudio.onerror = (e) => {
        console.warn('Phonetic audio error event in popover, falling back to speech synthesis:', e);
        fallbackToSpeech(e);
      };

      nextAudio.play().catch((error) => {
        console.warn('Error playing popover phonetic audio, falling back to speech synthesis:', error);
        fallbackToSpeech(error);
      });
    } else {
      speak(word, accentLang);
    }
  };

  const handleSave = async () => {
    if(!currentUser) {
      setLoginModal(true);
      return;
    }
    if (!word) return;

    setIsSaving(true);
    setErrorMessage(null);
    
    try {
      if (onSaveToSession) {
        await onSaveToSession({
          word,
          isExpression,
          assignedTo: sessionAssignee,
          details
        });
        const targetLabel = sessionAssignee === 'both' ? 'Both' : sessionAssignee === 'me' ? 'You' : sessionPartner?.name || 'Friend';
        setErrorMessage(`Saved for ${targetLabel}! 🎯`);
        setWordSaved(true);
      } else {
        if (isExpression) {
          await addExpression(word);
          setErrorMessage('Expression saved successfully!');
        } else {
          await addWord(word);
          setErrorMessage('Word saved successfully!');
        }
        setWordSaved(true);
      }
    } catch (err) {
      console.error('Error saving word:', err);
      setErrorMessage('Failed to save word. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveWithAssignee = async (assignee: SessionAssignee) => {
    setSessionAssignee(assignee);
    if (!currentUser) {
      setLoginModal(true);
      return;
    }
    if (!word) return;

    setIsSaving(true);
    setErrorMessage(null);

    try {
      if (onSaveToSession) {
        await onSaveToSession({
          word,
          isExpression,
          assignedTo: assignee,
          details
        });
        const targetLabel = assignee === 'both' ? 'Both' : assignee === 'me' ? 'You' : sessionPartner?.name || 'Friend';
        setErrorMessage(`Saved for ${targetLabel}! 🎯`);
        setWordSaved(true);
      } else {
        await handleSave();
      }
    } catch (err) {
      console.error('Error saving word:', err);
      setErrorMessage('Failed to save word. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleGetWord = async () => {
    if (!word) return;
    try {
      const wordData = isExpression ? await getExpression(word) : await getWord(word);
      if(wordData) {
        setWordSaved(true);
      } else {
        setWordSaved(false);
      }
    } catch (err) {
      console.error('Error getting word/expression in popover:', err);
      setWordSaved(false);
    }
  };

  useEffect(() => {
    if(word) {
      handleGetWord()
    }
  }, [word]);

  const isLongText = isExpression || Boolean(word && (word.split(/\s+/).length > 2 || word.length > 20));

  return (
    <div 
      ref={popoverRef}
      className={
        inline
          ? 'relative w-full h-fit bg-white rounded-xl p-3.5 sm:p-4 border border-purple-100 shadow-xs'
          : `${useRelativePosition ? 'relative' : 'fixed'} z-[9999] h-fit bg-white rounded-2xl shadow-xl p-4 sm:p-5 w-full sm:w-96 md:w-[420px] max-w-[calc(100vw-24px)] border border-purple-100`
      }
      style={useRelativePosition || inline ? {} : {
        top: `${position.top}px`,
        left: `clamp(16px, ${position.left}px, calc(100vw - 440px))`,
      }}
    >
      <Modal 
        isOpen={loginModal} 
        setIsModalOpen={setLoginModal} 
        title='You need to have an account!'
        message='Create an account to save your words, follow your progress and share with your friends!'
        actionText='Login/Register'
        action={() => navigate('/login')}
        />
      <div className={`mb-3.5 ${isLongText ? 'space-y-2.5' : 'flex items-center justify-between gap-2'}`}>
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 break-words leading-snug">
            {word}
          </h3>
          <button
            onClick={onClose}
            className={`p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors shrink-0 ${isLongText ? '-mr-1 -mt-1' : 'ml-auto'}`}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center flex-wrap gap-1.5 sm:gap-2">
          <button
            onClick={handleSave}
            disabled={isSaving || wordSaved}
            className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${
              wordSaved
                ? 'bg-purple-100 text-purple-700'
                : 'text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200'
            }`}
          >
            {isSaving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-700" />
            ) : wordSaved ? (
              <>
                <BookMarked className="w-4 h-4 text-purple-700" data-tooltip-id="saved"/>
                <span className="text-xs">Saved</span>
                <Tooltip id="saved" place="top" content="Word Saved" />
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 text-purple-700" data-tooltip-id="save" />
                <span className="text-xs">Save</span>
                <Tooltip id="save" place="top" content="Save word" />
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate(isExpression ? '/expressions' : '/vocabulary', { state: { word } })}
            className="p-1.5 text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors flex items-center justify-center"
            title="More Details"
            data-tooltip-id="definitions"
          >
            <BookOpen className="w-4 h-4" />
          </button>
          <Tooltip id="definitions" place="top" content="More Details" />
          <button
            onClick={() => handleSpeak('en-US')}
            className="px-2 py-1 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors flex items-center gap-1"
            aria-label="Listen to pronunciation"
            data-testid="popover-speak-us"
            title="Listen in US English"
          >
            <Volume2 className="w-3.5 h-3.5" />
            <span>US</span>
          </button>
          <button
            onClick={() => handleSpeak('en-GB')}
            className="px-2 py-1 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg transition-colors flex items-center gap-1"
            aria-label="Listen to British pronunciation"
            data-testid="popover-speak-uk"
            title="Listen in British English"
          >
            <Volume2 className="w-3.5 h-3.5" />
            <span>UK</span>
          </button>
        </div>
      </div>

      {onSaveToSession && (
        <div className="mb-3 bg-purple-50 p-2.5 rounded-xl border border-purple-100">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-bold text-purple-900">
              Save in Session to:
            </p>
            <span className="text-[10px] text-purple-600 font-semibold">
              Click to save directly
            </span>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <button
              type="button"
              onClick={() => handleSaveWithAssignee('me')}
              disabled={isSaving}
              className={`text-xs py-1.5 px-1 rounded-lg font-bold transition-all flex items-center justify-center gap-1 ${
                sessionAssignee === 'me' && wordSaved
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'bg-white text-purple-700 hover:bg-purple-100 border border-purple-200 shadow-2xs'
              }`}
            >
              <span>Me</span>
              {sessionAssignee === 'me' && wordSaved && <span className="text-[10px]">✓</span>}
            </button>
            <button
              type="button"
              onClick={() => handleSaveWithAssignee('friend')}
              disabled={isSaving}
              className={`text-xs py-1.5 px-1 rounded-lg font-bold transition-all truncate flex items-center justify-center gap-1 ${
                sessionAssignee === 'friend' && wordSaved
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'bg-white text-purple-700 hover:bg-purple-100 border border-purple-200 shadow-2xs'
              }`}
              title={sessionPartner?.name || 'Friend'}
            >
              <span className="truncate">{sessionPartner?.name ? sessionPartner.name.split(' ')[0] : 'Friend'}</span>
              {sessionAssignee === 'friend' && wordSaved && <span className="text-[10px]">✓</span>}
            </button>
            <button
              type="button"
              onClick={() => handleSaveWithAssignee('both')}
              disabled={isSaving}
              className={`text-xs py-1.5 px-1 rounded-lg font-bold transition-all flex items-center justify-center gap-1 ${
                sessionAssignee === 'both' && wordSaved
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'bg-white text-purple-700 hover:bg-purple-100 border border-purple-200 shadow-2xs'
              }`}
            >
              <span>Both</span>
              {sessionAssignee === 'both' && wordSaved && <span className="text-[10px]">✓</span>}
            </button>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="mb-2 text-xs py-1 px-2 rounded bg-purple-100 text-purple-800 font-medium">
          {errorMessage}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center p-4">
          <Loader className="w-6 h-6 animate-spin text-purple-600" />
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      ) : details && (
        <div className="space-y-3">
          {details.phonetic && (
            <div>
              <h4 className="text-sm font-medium text-gray-600">Pronunciation:</h4>
              <p className="text-sm text-gray-800">{details.phonetic}</p>
            </div>
          )}
          <div>
            <h4 className="text-sm font-medium text-gray-600">Definition:</h4>
            <p className="text-sm text-gray-800">{details.definition}</p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-600">Portuguese:</h4>
            <p className="text-sm text-gray-800">{details.translation}</p>
          </div>
          {details.contextTranslation && (
            <div>
              <h4 className="text-sm font-medium text-gray-600">Sentence Translation:</h4>
              <p className="text-sm text-gray-800">{details.contextTranslation}</p>
            </div>
          )}
          {details.examples.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-600">Example:</h4>
              <p className="text-sm text-gray-800 italic">
                "{details.examples[0].replace(/{\/?it}|\/it\//g, '').replace(/{\/?phrase}/g, '')}"
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}